import type { RouteLocationRaw } from "vue-router";
import type { Image } from "@/services/images";
import type { Price } from "@/services/prices/priceTypes";
import type { CategoryId } from "@/services/collection/categories/categoriesSchema";
import type { RaritySymbolId } from "@/services/parameters/pokemonRaritySymbol";
import type { FoilVariant } from "@/services/collectibles/foilVariants";

/**
 * Grid vs. table listing layout. String values are the representation persisted in the
 * shared preferences cookie — see `useItemListLayout`.
 */
export enum ItemListLayout {
  Grid = "grid",
  Table = "table",
}

/**
 * Listing area whose grid/table choice is remembered on its own — the preference is
 * per section, not one setting shared across the whole site. "Moje předměty" is a single
 * section across all five status tabs. "CuratedList" is the Master set progress view
 * (cards/rows there map onto Grid/Table).
 */
export enum ItemListSection {
  Marketplace = "marketplace",
  Database = "database",
  MyItems = "myItems",
  CuratedList = "curatedList",
}

/**
 * One segment of the parameter summary line shown under an item's name. Rendered as plain
 * text, joined by a separator; a segment with a `tooltip` reveals it on hover (e.g. the full
 * condition name behind a short grading code like "MT/NM").
 */
export type ItemParamSummaryEntry = {
  text: string;
  tooltip?: string;
  /** When set, the segment renders this Pokémon rarity symbol instead of `text` (`text` stays the label). */
  raritySymbol?: RaritySymbolId;
};

/** Owner shown on a card/row — only where the listing is another collector's (marketplace, public). */
export type ItemOwnerVm = {
  nickname: string;
  avatarUrl: string | null;
  rating: number;
  ratingCount: number;
};

/**
 * Normalized, presentation-ready view of one listing item. Both `ItemCard` and `ItemRow`
 * render this shape and nothing else; context-specific adapter composables
 * (`useCollectionItemVm` / `useCollectibleVm`) build it from `CollectionItemElastic` /
 * `CollectibleElastic`. Anything context-specific (likes, bulk selection, CTAs) is passed
 * in through slots, not this model.
 */
export type ItemVm = {
  id: number;
  name: string;
  /** Detail-page target. `undefined` while an adapter is still resolving it (e.g. the catalogue
   * link needs the category to load) — the card/row then renders the name as plain text. */
  link?: RouteLocationRaw;
  categoryId?: CategoryId;
  image?: Image;
  /** Short serialized parameter list shown under the name, e.g. ["PSA 10", "2021", "/99"]. */
  paramSummary: ItemParamSummaryEntry[];
  /** Primary price, emphasized in the card footer / a dedicated table column. */
  price?: Price;
  owner?: ItemOwnerVm;
  /**
   * Per-foil catalogue prices of the item's collectible (Pokémon only), listed under the primary
   * price. Behind the experimental-features flag; only the marketplace card fills it.
   */
  collectibleFoilPrices?: FoilVariant[];
};
